import React, { useState, useEffect, useRef, Fragment } from 'react'
import { Formik } from 'formik'
import classNames from 'classnames'
import { useErrorService, useNotificationService } from '@abroad/components'
import { ExperienceInputField } from '../form'
import ExperienceCheckbox from '../form/ExperienceCheckbox'
import PropTypes from 'prop-types'
import EnrollmentSubSections from './EnrollmentSubSections'

const stepConfig = [
  {
    key: 'step1',
    label: 'Enrollment form',
  },
  {
    key: 'step2',
    label: 'Payment',
  },
  {
    key: 'step3',
    label: 'Travel document',
  },
  {
    key: 'step4',
    label: 'Experience coaching',
  },
  {
    key: 'step5',
    label: 'Call & explore platform',
  },
]

const getDefaultStepData = (data = {}) => {
  return {
    step1: {
      enabled: data?.step1?.enabled !== undefined ? data.step1.enabled : true,
      title: '',
      description: '',
      ...data.step1,
    },
    step2: {
      enabled: data?.step2?.enabled !== undefined ? data.step2.enabled : true,
      title: '',
      description: '',
      ...data.step2,
    },
    step3: {
      title: '',
      description: '',
      flightInstructions: {
        enabled:
          data?.step3?.subStep1?.enabled !== undefined
            ? data.step3.subStep1.enabled
            : data?.step3?.flightInstructions?.enabled !== undefined
            ? data.step3.flightInstructions.enabled
            : true,
        generalInstructions:
          data?.step3?.subStep1?.flightInstructions?.generalInstructions || '',
        arrivalInstructions: {
          title:
            data?.step3?.subStep1?.flightInstructions?.arrivalInstructions
              ?.title || 'Arrival Instructions',
          content:
            data?.step3?.subStep1?.flightInstructions?.arrivalInstructions
              ?.content || '',
        },
        returnInstructions: {
          title:
            data?.step3?.subStep1?.flightInstructions?.returnInstructions
              ?.title || 'Return Instructions',
          content:
            data?.step3?.subStep1?.flightInstructions?.returnInstructions
              ?.content || '',
        },
      },
      // Merged step 4 fields into step 3
      travelInsurance: {
        enabled:
          data?.step3?.subStep2?.enabled !== undefined
            ? data.step3.subStep2.enabled
            : data?.step3?.travelInsurance?.enabled !== undefined
            ? data.step3.travelInsurance.enabled
            : true,
        title: data?.step3?.subStep2?.title || '',
        description: data?.step3?.subStep2?.description || '',
      },
      // Passport section - description only
      passport: {
        enabled:
          data?.step3?.subStep3?.enabled !== undefined
            ? data.step3.subStep3.enabled
            : data?.step3?.passport?.enabled !== undefined
            ? data.step3.passport.enabled
            : true,
        description: data?.step3?.subStep3?.description || '',
      },
      // Headshot section - description only
      headshot: {
        enabled:
          data?.step3?.subStep4?.enabled !== undefined
            ? data.step3.subStep4.enabled
            : data?.step3?.headshot?.enabled !== undefined
            ? data.step3.headshot.enabled
            : true,
        description: data?.step3?.subStep4?.description || '',
      },
      ...data.step3,
      // Ensure enabled is set after spread to maintain default
      enabled: data?.step3?.enabled !== undefined ? data.step3.enabled : true,
    },
    step4: {
      title: '',
      description: '',
      // Map old step5 data to step4
      ...data.step4,
      // Ensure enabled is set after spread to maintain default
      enabled: data?.step4?.enabled !== undefined ? data.step4.enabled : true,
    },
    step5: {
      title: '',
      description: '',
      // Map old step5 data to step4
      ...data.step5,
      // Ensure enabled is set after spread to maintain default
      enabled: data?.step5?.enabled !== undefined ? data.step5.enabled : true,
    },
  }
}

const QuestEnrollmentProcess = ({ questDetail, addUpdateQuest }) => {
  const [stepData, setStepData] = useState({})
  const [activeTab, setActiveTab] = useState(null)
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const [isAutoSaving, setIsAutoSaving] = useState(false)
  const [lastSavedStepData, setLastSavedStepData] = useState({})
  const Error = useErrorService()
  const Notification = useNotificationService()
  const formikRef = useRef()

  // Helper function to check if all sub-sections are disabled
  const areAllSubSectionsDisabled = (step3Data) => {
    if (!step3Data) return false

    const flightInstructions = step3Data.flightInstructions?.enabled !== false
    const travelInsurance = step3Data.travelInsurance?.enabled !== false
    const passport = step3Data.passport?.enabled !== false
    const headshot = step3Data.headshot?.enabled !== false

    // All sub-sections are disabled if none are enabled
    return !flightInstructions && !travelInsurance && !passport && !headshot
  }

  // Callback to check and auto-disable main step if all sub-sections are disabled
  const checkAndDisableMainStepIfNeeded = async (updatedStepData) => {
    if (
      updatedStepData.step3 &&
      updatedStepData.step3.enabled &&
      areAllSubSectionsDisabled(updatedStepData.step3)
    ) {
      // Automatically disable the main step
      updatedStepData.step3.enabled = false
      setStepData({ ...updatedStepData })

      if (formikRef.current) {
        formikRef.current.setFieldValue('step3.enabled', false)
      }

      Notification.showNotification(
        'All sub-sections are disabled. The Travel document step has been automatically disabled.',
        'danger',
      )

      // Save the updated data
      try {
        await updateQuestDetails(updatedStepData)
        setLastSavedStepData(updatedStepData)
      } catch (error) {
        Error.showError(error)
      }
    }
  }

  useEffect(() => {
    if (questDetail?.stepConfiguration) {
      const merged = getDefaultStepData(questDetail.stepConfiguration)
      setStepData(merged)
      setLastSavedStepData(merged)
    }
  }, [questDetail])

  // This function updates the whole enrollment process config (all steps)
  const updateQuestDetails = async (updatedStepData) => {
    setIsFormSubmitting(true)
    try {
      // Ensure step3 flightInstructions and travelInsurance data is valid before sending
      if (
        updatedStepData.step3?.flightInstructions ||
        updatedStepData.step3?.travelInsurance
      ) {
        // Deep clone to avoid mutating the original data
        const cleanedStepData = JSON.parse(JSON.stringify(updatedStepData))

        if (cleanedStepData.step3) {
          const step3 = cleanedStepData.step3

          cleanedStepData.step3 = {
            enabled: step3.enabled || false,
            title: step3.title || '',
            description: step3.description || '',
            subStep1: {
              enabled:
                step3.flightInstructions?.enabled !== undefined
                  ? step3.flightInstructions.enabled
                  : true,
              flightInstructions: {
                generalInstructions:
                  step3.flightInstructions?.generalInstructions || '',
                arrivalInstructions: {
                  title:
                    step3.flightInstructions?.arrivalInstructions?.title ||
                    'Arrival Instructions',
                  content:
                    step3.flightInstructions?.arrivalInstructions?.content ||
                    '',
                },
                returnInstructions: {
                  title:
                    step3.flightInstructions?.returnInstructions?.title ||
                    'Return Instructions',
                  content:
                    step3.flightInstructions?.returnInstructions?.content || '',
                },
              },
            },
            subStep2: {
              enabled:
                step3.travelInsurance?.enabled !== undefined
                  ? step3.travelInsurance.enabled
                  : true,
              title: step3.travelInsurance?.title || '',
              description: step3.travelInsurance?.description || '',
            },
            subStep3: {
              enabled:
                step3.passport?.enabled !== undefined
                  ? step3.passport.enabled
                  : true,
              description: step3.passport?.description || '',
            },
            subStep4: {
              enabled:
                step3.headshot?.enabled !== undefined
                  ? step3.headshot.enabled
                  : true,
              description: step3.headshot?.description || '',
            },
          }
        }

        const payload = {
          stepConfiguration: {
            ...cleanedStepData,
            step3: cleanedStepData.step3, // already transformed
          },
          id: questDetail.id,
        }
        await addUpdateQuest(payload)
        setLastSavedStepData(cleanedStepData)
      } else {
        // For other steps, proceed normally
        const payload = {
          stepConfiguration: updatedStepData,
          id: questDetail.id,
        }
        await addUpdateQuest(payload)
        setLastSavedStepData(updatedStepData)
      }
    } catch (e) {
      Error.showError(e)
    }
    setIsFormSubmitting(false)
  }

  // Save only the current step, but send the whole config as payload
  const handleStepSave = async (values, stepKey) => {
    // For step3, ensure flightInstructions and travelInsurance content is always structured properly
    if (stepKey === 'step3' && values[stepKey]) {
      // Create a deep copy to avoid mutating the original values
      const updatedValues = JSON.parse(JSON.stringify(values))

      if (updatedValues[stepKey].flightInstructions) {
        const flightInstructions = updatedValues[stepKey].flightInstructions
        // Ensure enabled is preserved
        flightInstructions.enabled =
          flightInstructions.enabled !== undefined
            ? flightInstructions.enabled
            : true
        // Ensure generalInstructions is a string
        flightInstructions.generalInstructions =
          flightInstructions.generalInstructions || ''

        // Ensure arrivalInstructions structure
        if (flightInstructions.arrivalInstructions) {
          flightInstructions.arrivalInstructions = {
            title:
              flightInstructions.arrivalInstructions.title ||
              'Arrival Instructions',
            content: flightInstructions.arrivalInstructions.content || '',
          }
        } else {
          flightInstructions.arrivalInstructions = {
            title: 'Arrival Instructions',
            content: '',
          }
        }

        // Ensure returnInstructions structure
        if (flightInstructions.returnInstructions) {
          flightInstructions.returnInstructions = {
            title:
              flightInstructions.returnInstructions.title ||
              'Return Instructions',
            content: flightInstructions.returnInstructions.content || '',
          }
        } else {
          flightInstructions.returnInstructions = {
            title: 'Return Instructions',
            content: '',
          }
        }
      }

      // Ensure travelInsurance structure
      if (updatedValues[stepKey].travelInsurance) {
        updatedValues[stepKey].travelInsurance = {
          enabled:
            updatedValues[stepKey].travelInsurance.enabled !== undefined
              ? updatedValues[stepKey].travelInsurance.enabled
              : true,
          title: updatedValues[stepKey].travelInsurance.title || '',
          description: updatedValues[stepKey].travelInsurance.description || '',
        }
      }

      // Ensure passport structure
      if (updatedValues[stepKey].passport) {
        updatedValues[stepKey].passport = {
          enabled:
            updatedValues[stepKey].passport.enabled !== undefined
              ? updatedValues[stepKey].passport.enabled
              : true,
          description: updatedValues[stepKey].passport.description || '',
        }
      }

      // Ensure headshot structure
      if (updatedValues[stepKey].headshot) {
        updatedValues[stepKey].headshot = {
          enabled:
            updatedValues[stepKey].headshot.enabled !== undefined
              ? updatedValues[stepKey].headshot.enabled
              : true,
          description: updatedValues[stepKey].headshot.description || '',
        }
      }

      // Update the values with cleaned data
      values = updatedValues
    }

    const updatedStepData = { ...stepData, [stepKey]: values[stepKey] }
    setStepData(updatedStepData)
    await updateQuestDetails(updatedStepData)
    setActiveTab(null)
  }

  // Cancel only the current step, revert to last saved state for that step
  const handleStepCancel = (resetForm, stepKey) => {
    const updatedStepData = { ...stepData }
    updatedStepData[stepKey] = lastSavedStepData[stepKey]
    setStepData(updatedStepData)
    resetForm({
      values: updatedStepData,
    })
    setActiveTab(null)
  }

  const renderStepContent = (
    stepKey,
    step,
    values,
    handleChange,
    setFieldValue,
    resetForm,
  ) => {
    if (!values[stepKey]) return null

    const isStepEnabled = values[stepKey].enabled

    return (
      <div className='experience-add-form'>
        <div className='experience-faq-editor'>
          <ExperienceCheckbox
            id={`${stepKey}-enabled`}
            name={`${stepKey}.enabled`}
            label='Enable this step'
            checked={isStepEnabled !== undefined ? isStepEnabled : true}
            disabled={false}
            onChange={async (e) => {
              const newEnabledValue = e.target.checked

              // Auto-save the checkbox change
              const updatedStepData = { ...stepData }
              updatedStepData[stepKey] = {
                ...updatedStepData[stepKey],
                enabled: newEnabledValue,
              }

              // If trying to enable step3, check if all sub-sections are disabled
              // If so, automatically enable all sub-sections
              if (stepKey === 'step3' && newEnabledValue) {
                const currentStep3Data = updatedStepData.step3
                if (areAllSubSectionsDisabled(currentStep3Data)) {
                  // Automatically enable all sub-sections
                  if (!updatedStepData.step3.flightInstructions) {
                    updatedStepData.step3.flightInstructions = {}
                  }
                  updatedStepData.step3.flightInstructions.enabled = true

                  if (!updatedStepData.step3.travelInsurance) {
                    updatedStepData.step3.travelInsurance = {}
                  }
                  updatedStepData.step3.travelInsurance.enabled = true

                  if (!updatedStepData.step3.passport) {
                    updatedStepData.step3.passport = {}
                  }
                  updatedStepData.step3.passport.enabled = true

                  if (!updatedStepData.step3.headshot) {
                    updatedStepData.step3.headshot = {}
                  }
                  updatedStepData.step3.headshot.enabled = true

                  // Update formik values for all sub-sections
                  setFieldValue(`${stepKey}.flightInstructions.enabled`, true)
                  setFieldValue(`${stepKey}.travelInsurance.enabled`, true)
                  setFieldValue(`${stepKey}.passport.enabled`, true)
                  setFieldValue(`${stepKey}.headshot.enabled`, true)
                }
              }

              setFieldValue(`${stepKey}.enabled`, newEnabledValue)
              setStepData(updatedStepData)

              setIsAutoSaving(true)
              try {
                await updateQuestDetails(updatedStepData)
                setLastSavedStepData(updatedStepData)
              } catch (error) {
                // Revert the change if save fails
                setFieldValue(`${stepKey}.enabled`, !newEnabledValue)
                if (stepKey === 'step3' && newEnabledValue) {
                  // Revert all sub-sections if they were auto-enabled
                  const currentStep3Data = stepData.step3 || values.step3
                  if (areAllSubSectionsDisabled(currentStep3Data)) {
                    setFieldValue(
                      `${stepKey}.flightInstructions.enabled`,
                      false,
                    )
                    setFieldValue(`${stepKey}.travelInsurance.enabled`, false)
                    setFieldValue(`${stepKey}.passport.enabled`, false)
                    setFieldValue(`${stepKey}.headshot.enabled`, false)
                  }
                }
                const revertedStepData = { ...stepData }
                revertedStepData[stepKey] = {
                  ...revertedStepData[stepKey],
                  enabled: !newEnabledValue,
                }
                if (
                  stepKey === 'step3' &&
                  newEnabledValue &&
                  areAllSubSectionsDisabled(stepData.step3 || values.step3)
                ) {
                  if (revertedStepData.step3.flightInstructions) {
                    revertedStepData.step3.flightInstructions.enabled = false
                  }
                  if (revertedStepData.step3.travelInsurance) {
                    revertedStepData.step3.travelInsurance.enabled = false
                  }
                  if (revertedStepData.step3.passport) {
                    revertedStepData.step3.passport.enabled = false
                  }
                  if (revertedStepData.step3.headshot) {
                    revertedStepData.step3.headshot.enabled = false
                  }
                }
                setStepData(revertedStepData)
                Error.showError(error)
              } finally {
                setIsAutoSaving(false)
              }
            }}
          />
          <p className='experience-f-hint is-plain mt-2'>
            When disabled, participants skip this step.
          </p>
          <div className='experience-f mt-12px'>
            <ExperienceInputField
              name={`${stepKey}.title`}
              label='Title'
              capitalizeFirstLetter={true}
              placeholder='Enter step title'
              formGroupProps={{ className: 'mb-0' }}
              showErrorMessage={false}
              displayFlex={false}
              disabled={!isStepEnabled}
            />
          </div>
          <div className='experience-f mt-12px'>
            <ExperienceInputField
              name={`${stepKey}.description`}
              label='Description'
              placeholder='Enter step description'
              capitalizeFirstLetter={true}
              as='textarea'
              rows={3}
              showErrorMessage={false}
              displayFlex={false}
              disabled={!isStepEnabled}
            />
          </div>
          {stepKey === 'step3' && (
            <EnrollmentSubSections
              stepKey={stepKey}
              values={values}
              isStepEnabled={isStepEnabled}
              stepData={stepData}
              setStepData={setStepData}
              setFieldValue={setFieldValue}
              setIsAutoSaving={setIsAutoSaving}
              updateQuestDetails={updateQuestDetails}
              Error={Error}
              checkAndDisableMainStepIfNeeded={checkAndDisableMainStepIfNeeded}
            />
          )}
        </div>
        <div className='experience-form-actions mt-12px'>
          <button
            type='button'
            className='btn-navy'
            disabled={isFormSubmitting || isAutoSaving}
            onClick={() => handleStepSave(values, stepKey)}>
            {isFormSubmitting && !isAutoSaving ? 'Saving…' : 'Save step'}
          </button>
          <button
            type='button'
            className='btn-outline-experience'
            onClick={() => handleStepCancel(resetForm, stepKey)}>
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Formik
        innerRef={formikRef}
        initialValues={getDefaultStepData(stepData)}
        enableReinitialize
        onSubmit={() => {}}>
        {({ handleChange, setFieldValue, values, resetForm }) => (
          <>
            <div className='experience-lrow-list'>
              {stepConfig.map((step, idx) => {
                const enabled =
                  values[step.key]?.enabled !== undefined
                    ? values[step.key].enabled
                    : true
                const title = values[step.key]?.title || step.label
                const isEditing = activeTab === step.key
                return (
                  <Fragment key={step.key}>
                    <div
                      className={classNames('experience-lrow', {
                        'is-editing': isEditing,
                      })}
                      role='button'
                      tabIndex={0}
                      onClick={() =>
                        setActiveTab((prev) =>
                          prev === step.key ? null : step.key,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setActiveTab((prev) =>
                            prev === step.key ? null : step.key,
                          )
                        }
                      }}>
                      <span className='experience-qnum'>{idx + 1}</span>
                      <span className='experience-lrow-copy'>
                        <b>{step.label}</b>
                        <small>{title}</small>
                      </span>
                      <span
                        className={
                          enabled ? 'experience-req on' : 'experience-req'
                        }>
                        {enabled ? 'Enabled' : 'Skipped'}
                      </span>
                    </div>
                    {isEditing
                      ? renderStepContent(
                          step.key,
                          step,
                          values,
                          handleChange,
                          setFieldValue,
                          resetForm,
                        )
                      : null}
                  </Fragment>
                )
              })}
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}

QuestEnrollmentProcess.propTypes = {
  questDetail: PropTypes.shape({
    id: PropTypes.string,
    stepConfiguration: PropTypes.shape({
      step1: PropTypes.shape({
        enabled: PropTypes.bool,
        title: PropTypes.string,
        description: PropTypes.string,
      }),
      step2: PropTypes.shape({
        enabled: PropTypes.bool,
        title: PropTypes.string,
        description: PropTypes.string,
      }),
      step3: PropTypes.shape({
        enabled: PropTypes.bool,
        title: PropTypes.string,
        description: PropTypes.string,
        flightInstructions: PropTypes.shape({
          generalInstructions: PropTypes.string,
          arrivalInstructions: PropTypes.shape({
            content: PropTypes.string,
          }),
          returnInstructions: PropTypes.shape({
            content: PropTypes.string,
          }),
        }),
        travelInsurance: PropTypes.shape({
          enabled: PropTypes.bool,
          title: PropTypes.string,
          description: PropTypes.string,
        }),
        passport: PropTypes.shape({
          enabled: PropTypes.bool,
          description: PropTypes.string,
        }),
        headshot: PropTypes.shape({
          enabled: PropTypes.bool,
          description: PropTypes.string,
        }),
      }),
      step4: PropTypes.shape({
        enabled: PropTypes.bool,
        title: PropTypes.string,
        description: PropTypes.string,
      }),
      step5: PropTypes.shape({
        enabled: PropTypes.bool,
        title: PropTypes.string,
        description: PropTypes.string,
      }),
    }),
  }),
  addUpdateQuest: PropTypes.func.isRequired,
}

export default QuestEnrollmentProcess
